import {objectFromEntries} from '@benchling/js-utils';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {NON_PERSISTED_GQL_FIELDS_FOR_CHART_VIEW_BY_CHART_TYPE} from 'analysis-tool/charting/AnalysisChartingConstants';
import {isNotAnalysisOnlyChartConfig} from 'analysis-tool/charting/AnalysisChartingUtils';
import {assert} from 'Asserts';
import {DataFrameFilterStepConfig} from 'data-frames/filters/DataFrameFilterTypes';
import {
  PipelineStepType,
  AnalyticsVisualizationAggregationType as AggregationType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';

export function getVisualizationConfigGQLFromChartStepConfig({
  config,
  id,
}: Pick<PipelineStep<PipelineStepType.CHART_VIEW, 0>, 'id' | 'config'>): {
  visualizationConfig: VisualizationConfig;
  visualizationName: string;
} {
  assert(config != null, 'Expected non-null chart config');
  assert(isNotAnalysisOnlyChartConfig(config.chartConfig.type), 'Expected non-analysis only chart step');
  return {
    visualizationConfig: {
      __typename: 'AnalyticsVisualizationNode',
      id: `stub-id-${id}`,
      parameters: {
        __typename: VISUALIZATION_TYPE_TO_PARAMETERS_TYPENAME[config.chartConfig.type],
        ...config.chartConfig.parameters,
        ...objectFromEntries(
          NON_PERSISTED_GQL_FIELDS_FOR_CHART_VIEW_BY_CHART_TYPE[config.chartConfig.type].map((field) => [
            field,
            null,
          ])
        ),
        // aggregation is not a chart configuration in CHART_VIEW steps, and default behavior is to combine
        // rows by summing the values of the measure column. Also since we can't condition the measure column
        // selector on aggregation value, we need to stub a non-count value in here.
        ...(config.chartConfig.type === VisualizationType.PIE_CHART
          ? {
              aggregationType: AggregationType.SUM,
            }
          : {}),
      } as AnalyticsVisualization['parameters'],
    },
    visualizationName: config.chartName,
  };
}

export function getVisualizationConfigGQLFromChartStep(step: GetAnalysisPipelineStep): {
  visualizationConfig: VisualizationConfig;
  visualizationName: string;
} {
  const config = getPipelineStepConfig(step, PipelineStepType.CHART_VIEW, 0);
  return getVisualizationConfigGQLFromChartStepConfig({config, id: step.id});
}

export function getFilterConfigFromStep(step: GetAnalysisPipelineStep): DataFrameFilterStepConfig {
  return getPipelineStepConfig(
    step,
    PipelineStepType.FILTER_DATASET,
    0
  ) as unknown as DataFrameFilterStepConfig;
}
